/**
 * QuestionScreen Component
 * Main question display with options and navigation
 */
import React from 'react';
import { Question } from '../../types';
import Timer from '../Timer';
import ProgressBar from './ProgressBar';

interface QuestionScreenProps {
  question: Question;
  selectedAnswer: string | null;
  loading: boolean;
  canGoBack: boolean;
  dimensionTimeRemaining: number | null;
  sectionId: string | null;
  onAnswerSelect: (answer: string) => void;
  onSubmit: () => void;
  onNavigate: (direction: 'back' | 'forward') => void;
  onQuit: () => void;
  onTimeExpired: () => void;
}

export const QuestionScreen: React.FC<QuestionScreenProps> = ({
  question,
  selectedAnswer,
  loading,
  canGoBack,
  dimensionTimeRemaining,
  sectionId,
  onAnswerSelect,
  onSubmit,
  onNavigate,
  onQuit,
  onTimeExpired
}) => (
  <div className="min-h-screen bg-neutral p-3">
    <div className="max-w-xl mx-auto pt-3">
      {/* Progress Bar */}
      <ProgressBar 
        current={question.current_index}
        total={question.total_questions}
        sectionName={question.section_name}
      />

      {/* Dimension Timer */}
      {dimensionTimeRemaining !== null && (
        <div className="mb-4 bg-white rounded-xl shadow-lg p-4">
          <p className="text-secondary font-semibold text-center text-sm mb-2">
            Temps pour cette dimension
          </p>
          <Timer 
            duration={dimensionTimeRemaining} 
            onComplete={onTimeExpired} 
            key={`dimension-${sectionId}`}
          />
        </div>
      )}

      {/* Question Card */}
      <div className="bg-white rounded-xl shadow-lg p-5">
        <div className="flex justify-between items-start mb-4">
          <h3 className="text-base font-semibold text-gray-800 flex-1 pr-3">
            {question.question_text}
          </h3>
          <button 
            onClick={onQuit} 
            className="text-red-500 text-sm hover:underline whitespace-nowrap"
          >
            Quitter
          </button>
        </div>

        {/* Options */}
        <div className="mb-5">
          <div className="flex flex-wrap justify-center gap-3">
            {question.options.map((option, idx) => (
              <OptionButton
                key={idx}
                option={option}
                isSelected={selectedAnswer === option}
                onSelect={() => onAnswerSelect(option)}
              />
            ))}
          </div>
        </div>

        {/* Navigation buttons */}
        <div className="flex gap-3">
          {canGoBack && (
            <button
              onClick={() => onNavigate('back')}
              disabled={loading}
              className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold py-2.5 rounded-lg text-sm transition disabled:opacity-50"
            >
              Précédente
            </button>
          )}
          <button
            onClick={onSubmit}
            disabled={loading}
            className="flex-1 bg-primary hover:bg-opacity-90 text-white font-bold py-2.5 rounded-lg text-sm transition disabled:opacity-50"
          >
            {loading ? 'Envoi...' : selectedAnswer ? 'Valider' : 'Passer'}
          </button>
        </div>
      </div>
    </div>
  </div>
);

// Option button sub-component
interface OptionButtonProps {
  option: string;
  isSelected: boolean;
  onSelect: () => void;
}

const OptionButton: React.FC<OptionButtonProps> = ({ option, isSelected, onSelect }) => (
  <label
    className={`inline-block px-5 py-2.5 border-2 rounded-lg cursor-pointer text-sm transition ${
      isSelected
        ? 'border-primary bg-primary bg-opacity-10'
        : 'border-gray-300 hover:border-gray-400'
    }`}
  >
    <input
      type="radio"
      name="answer"
      value={option}
      checked={isSelected}
      onChange={onSelect}
      className="mr-2"
    />
    <span className="text-gray-800">{option}</span>
  </label>
);

export default QuestionScreen;
